/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 *
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.4.0
 */

import { Fragment, useCallback, useRef } from 'react';

import debounce from 'lodash/debounce';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Checkbox, DropDownFilter, RadioButton, RangeDatePicker } from '../../../components/common';
import { FlexStyled } from '../../../components/common/Block/Flexbox';
import { LayoutStyled } from '../../../components/common/Block/Layout';
import { FormBlockStyled, FormLabelStyled } from '../../../components/common/FormStyled/FormStyled';
import {
    makeSelectCallsDetailTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { ICallsDetailTranslation } from '../../../modules/internationalization/types/wizards/callsDetail';
import { IRootReducer } from '../../../reducers/rootReducer';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { callsDetailSetParams, callsDetailStorageSave, callsDetailValidateStart } from '../callsDetailActions';
import { ICallsDetailState } from '../callsDetailReducer';
import { makeSelectCallsDetailParams } from '../callsDetailSelectors';
import { ICallsDetailSetParamsPayload } from '../callsDetailTypes';
import { WizardCallsDetailPeriodStyled } from './styled/WizardCallsDetailsStyled';

export type TWizardCallsDetailStepPrepareProps = IConnectedProps & IDispatchProps;

export const WizardCallsDetailStepPrepare = (props: TWizardCallsDetailStepPrepareProps) => {
    const calendarRef = useRef<RangeDatePicker>(null);

    const okButtonHandler = useCallback(() => {
        if (calendarRef.current) {
            calendarRef.current.onApply();
        }
    }, [calendarRef]);

    const setRangeHandler = (leftRange: Moment, rightRange: Moment) => {
        props.callsDetailSetParams({
            dateFrom: leftRange.format(EFormatData.FullDataWithTimeZone),
            dateTo: rightRange.format(EFormatData.FullDataWithTimeZone),
        });
        delayedProcessAmount();
    };

    const setReportFormat = (format: string) => {
        props.callsDetailSetParams({
            reportFormat: format,
        });
        delayedProcessAmount();
    };

    const setAccountTaken = (type: string, value: boolean) => {
        props.callsDetailSetParams({
            [type]: value,
        });
        delayedProcessAmount();
    };

    const delayedProcessAmount = useCallback(debounce(() => {
        props.callsDetailValidateStart();
        props.callsDetailStorageSave();
    }, 300), [props.callsDetailValidateStart, props.callsDetailStorageSave]);

    return <LayoutStyled className="mt-3">

        <FormBlockStyled>
            <FormLabelStyled>{props.callsDetailTranslation.periodLabel}</FormLabelStyled>

            <WizardCallsDetailPeriodStyled>
                <DropDownFilter
                    widthAuto
                    filterButtonLabel={
                        `${moment(props.callsDetailParams.dateFrom)
                            .format(EFormatData.FullData)} — ${moment(props.callsDetailParams.dateTo)
                            .format(EFormatData.FullData)}`
                    }
                    icon={icons.calendarIcon}
                    iconOpened={icons.calendarIcon}
                    kind={'normal'}
                    showCancelBtn={true}
                    translation={props.translation.dropDown}
                    onOkClick={okButtonHandler}
                >
                    <RangeDatePicker
                        endDate={moment(props.callsDetailParams.dateTo)}

                        maxDate={moment()}
                        maxYear={moment().format('YYYY')}

                        messages={props.translation.calendar}
                        minDate={moment(props.callsDetailParams.minDate)}

                        minYear={moment().subtract(6, 'months').format('YYYY')}
                        ref={calendarRef}

                        startDate={moment(props.callsDetailParams.dateFrom)}
                        timePicker={false}

                        onApply={setRangeHandler}
                    />
                </DropDownFilter>
            </WizardCallsDetailPeriodStyled>
        </FormBlockStyled>

        {
            props.callsDetailParams.chooseAccountTakenEnabled &&
            <FormBlockStyled>
                <FormLabelStyled>{props.callsDetailTranslation.accountTaken.label}</FormLabelStyled>

                <LayoutStyled className="mb-3">
                    <Checkbox checked={props.callsDetailParams.accountTakenCorporate}
                              dataName="accountTakenCorporate"
                              label={props.callsDetailTranslation.accountTaken.corporate}
                              onChange={(value) => {
                                  setAccountTaken('accountTakenCorporate', value);
                              }}/>
                </LayoutStyled>
                <LayoutStyled className="mb-3">
                    <Checkbox checked={props.callsDetailParams.accountTakenPersonal}
                              dataName="accountTakenPersonal"
                              label={props.callsDetailTranslation.accountTaken.personal}
                              onChange={(value) => {
                                  setAccountTaken('accountTakenPersonal', value);
                              }}/>
                </LayoutStyled>
            </FormBlockStyled>
        }

        <FormBlockStyled>
            <FormLabelStyled>{props.callsDetailTranslation.formatLabel}</FormLabelStyled>

            <FlexStyled>
                {
                    props.callsDetailParams.formatsAvailable.map(format => <Fragment key={format}>
                        <LayoutStyled className="mr-4"
                                      data-container="calls-detail-report-format"
                                      onClick={() => {
                                          setReportFormat(format);
                                      }}>
                            <RadioButton checked={props.callsDetailParams.reportFormat === format}
                                         label={format}/>
                        </LayoutStyled>
                    </Fragment>)
                }
            </FlexStyled>
        </FormBlockStyled>

    </LayoutStyled>;
};

interface IConnectedProps {
    callsDetailParams: ICallsDetailState;
    callsDetailTranslation: ICallsDetailTranslation;
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    callsDetailParams: makeSelectCallsDetailParams(),
    callsDetailTranslation: makeSelectCallsDetailTranslation(),
    translation: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    callsDetailSetParams: (payload: ICallsDetailSetParamsPayload) => void;
    callsDetailStorageSave: () => void;
    callsDetailValidateStart: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    callsDetailSetParams: (payload) => dispatch(callsDetailSetParams(payload)),
    callsDetailStorageSave: () => dispatch(callsDetailStorageSave()),
    callsDetailValidateStart: () => dispatch(callsDetailValidateStart()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardCallsDetailStepPrepare);
